import {
    Component,
    OnDestroy,
    ChangeDetectionStrategy,
    inject,
} from '@angular/core';
import {
    FormControl,
    FormGroup,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { HandedOverCellRendererParams } from './models/handed-over-cell-renderer-params';
import {
    EMPTY,
    Observable,
    Subject,
    filter,
    switchMap,
    takeUntil,
    tap,
} from 'rxjs';
import { HandedOverFormModel } from './models/handed-over-form-model';
import { FormGroupType } from '../../../../../../core/models/form-group-type';
import { DropdownOption } from '../../../../../../core/models/dropdown-option';
import { ServiceFulfillmentService } from '../../../../../../modules/service-fulfillment/services/service-fulfillment.service';
import { MatDialog } from '@angular/material/dialog';
import { ModalComponent } from '../../../../modal/modal.component';

@Component({
    selector: 'app-handed-over-cell-renderer',
    imports: [MatSelectModule, MatFormFieldModule, ReactiveFormsModule],
    changeDetection: ChangeDetectionStrategy.Eager,
    templateUrl: './handed-over-cell-renderer.component.html',
})
export class HandedOverCellRendererComponent
    implements ICellRendererAngularComp, OnDestroy
{
    private destroy$ = new Subject<void>();

    params!: HandedOverCellRendererParams;

    handedOverDropdownOptions: DropdownOption[] = [];

    form: FormGroup<FormGroupType<HandedOverFormModel>> =
        this.getFormDefinition();

    private readonly serviceFulfillmentService = inject(
        ServiceFulfillmentService,
    );
    private readonly dialog = inject(MatDialog);

    agInit(params: HandedOverCellRendererParams): void {
        this.params = params;

        this.bindForm(params);
        this.setupFormListeners();
        this.setupDropdownOptions();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    refresh(): boolean {
        return false;
    }

    private bindForm(params: HandedOverCellRendererParams): void {
        this.form.patchValue({
            isHandedOver: params.data.isHandedOver,
        });

        // Disable the dropdown if the value is already true (handed over)
        if (params.data.isHandedOver === true) {
            this.form.controls.isHandedOver.disable();
        }
    }

    private getFormDefinition(): FormGroup<FormGroupType<HandedOverFormModel>> {
        return new FormGroup<FormGroupType<HandedOverFormModel>>({
            isHandedOver: new FormControl<boolean>(false, Validators.required),
        });
    }

    private saveFormDataToApi(): Observable<void> {
        return this.serviceFulfillmentService.submitToEwa(
            this.params.data.applicationId,
        );
    }

    private setupFormListeners(): void {
        this.form.valueChanges
            .pipe(
                takeUntil(this.destroy$),
                switchMap(() => this.displayHandOverWarningDialog()),
                tap((confirmed) => {
                    if (!confirmed) {
                        this.form.patchValue(
                            { isHandedOver: this.params.data.isHandedOver },
                            { emitEvent: false },
                        );
                    }
                }),
                filter((confirmed) => confirmed === true),
                switchMap(() => this.saveFormDataToApi()),
                tap(() => {
                    this.submitToEwaAndRefreshGrid();
                }),
            )
            .subscribe();
    }

    private displayHandOverWarningDialog(): Observable<boolean | undefined> {
        if (this.dialog.openDialogs.length > 0) {
            return EMPTY;
        }

        return this.dialog
            .open(ModalComponent, {
                disableClose: true,
                data: {
                    title: 'Hand Over to EWA?',
                    content:
                        "You are handing over an application from a customer's dashboard to EWA. This change cannot be reversed. Are you sure you want to proceed?",
                    primaryAction: {
                        title: 'Hand Over to EWA',
                        name: 'handOverToEwa',
                        classList: '!bg-primary-700 !text-tertiary-50',
                    },
                    isShowCancel: true,
                },
            })
            .afterClosed();
    }

    /**
     * Refresh the grid because fields may or may not have changed (e.g. Advisor, Order Id, etc.)
     */
    private submitToEwaAndRefreshGrid(): void {
        // Notify the parent to refresh grid after successful handover.
        this.params.onHandedOver();
    }

    private setupDropdownOptions(): void {
        this.handedOverDropdownOptions = this.params.dropdownOptions;
    }
}
